// src/pages/finance-portal/inventory/InventoryLevels.js
// Multi-location stock grid + transfer dialog.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem,
    Paper, Select, Skeleton, Stack, Switch, Table, TableBody, TableCell,
    TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK, formatCurrency, formatQty,
} from './components/inventoryFormatters';

export default function InventoryLevels() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [locations, setLocations] = useState([]);
    const [products, setProducts] = useState([]);
    const [locationFilter, setLocationFilter] = useState('');
    const [productFilter, setProductFilter] = useState(null);
    const [lowStockOnly, setLowStockOnly] = useState(false);

    const [transferDialog, setTransferDialog] = useState({
        open: false, product_id: null, variant_id: null,
        from_location_id: null, to_location_id: null, quantity: '', memo: '',
    });
    const [transferring, setTransferring] = useState(false);
    const productSearchTimer = useRef(null);

    const fetchLevels = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const params = { page: page + 1, per_page: perPage };
            if (locationFilter) params.location_id = locationFilter;
            if (productFilter?.id) params.product_id = productFilter.id;
            if (lowStockOnly) params.low_stock = true;
            const res = await axiosInstance.get('/api/finance/inventory/levels', { params });
            if (res.data?.success) {
                setRows(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [page, perPage, locationFilter, productFilter, lowStockOnly]);

    useEffect(() => { fetchLevels(); }, [fetchLevels]);

    useEffect(() => {
        axiosInstance.get('/api/finance/inventory/locations').then((r) => setLocations(r.data?.data || [])).catch(() => {});
    }, []);

    const handleProductSearch = (v) => {
        if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
        productSearchTimer.current = setTimeout(() => {
            axiosInstance.get('/api/finance/inventory/products', { params: { search: v, per_page: 50 } })
                .then((r) => setProducts(r.data?.data || [])).catch(() => {});
        }, 300);
    };

    const openTransfer = (row) => setTransferDialog({
        open: true, product_id: row.product_id, variant_id: row.variant_id,
        from_location_id: row.location_id, to_location_id: null,
        quantity: '', memo: '',
    });

    const handleTransfer = async () => {
        if (!transferDialog.to_location_id || !transferDialog.quantity) {
            alert('Destination and quantity required');
            return;
        }
        setTransferring(true);
        try {
            await axiosInstance.post('/api/finance/inventory/transfers', transferDialog);
            setTransferDialog({ open: false });
            fetchLevels();
        } catch (e) {
            alert(e.response?.data?.message || 'Transfer failed');
        } finally {
            setTransferring(false);
        }
    };

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                    Stock Levels
                </Typography>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                    On-hand, reserved, available, and on-order quantities by location
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Location</InputLabel>
                        <Select value={locationFilter} label="Location"
                            onChange={(e) => { setLocationFilter(e.target.value); setPage(0); }}>
                            <MenuItem value="">All</MenuItem>
                            {locations.map((l) => (
                                <MenuItem key={l.id} value={l.id}>{l.code} — {l.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Autocomplete
                        size="small" sx={{ minWidth: 320, flex: 1 }}
                        options={products}
                        getOptionLabel={(o) => `${o.sku} — ${o.name}`}
                        value={productFilter}
                        onChange={(e, v) => { setProductFilter(v); setPage(0); }}
                        onInputChange={(e, v) => handleProductSearch(v)}
                        renderInput={(params) => <TextField {...params} label="Product" />}
                    />
                    <FormControlLabel
                        control={<Switch checked={lowStockOnly} onChange={(e) => { setLowStockOnly(e.target.checked); setPage(0); }} />}
                        label="Low stock only"
                    />
                </Stack>
            </Paper>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>SKU</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Location</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">On Hand</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Reserved</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Available</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">On Order</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Value</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from({ length: 8 }).map((_, i) => (
                                <TableRow key={i}><TableCell colSpan={10}><Skeleton height={32} /></TableCell></TableRow>
                            ))
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                    No stock to show
                                </TableCell>
                            </TableRow>
                        ) : rows.map((r) => (
                            <TableRow key={r.id} hover>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{r.product_sku}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>
                                    {r.product_name} {r.variant_name && <Chip size="small" label={r.variant_name} sx={{ ml: 1 }} />}
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>
                                    {r.location_code} <span style={{ color: '#64748b' }}>{r.location_name}</span>
                                </TableCell>
                                <TableCell sx={{
                                    fontFamily: FONT_FAMILY, fontWeight: r.needs_reorder ? 700 : 500,
                                    color: r.needs_reorder ? '#dc2626' : '#0f172a',
                                }} align="right">{formatQty(r.quantity_on_hand)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(r.quantity_reserved)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(r.quantity_available)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(r.quantity_on_order)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">{formatCurrency(r.value_on_hand)}</TableCell>
                                <TableCell>
                                    {r.needs_reorder && (
                                        <Chip size="small" icon={<WarningAmberIcon />} label="Reorder" color="warning"
                                            sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} />
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openTransfer(r)} title="Transfer">
                                        <SwapHorizIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div" count={total} page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    sx={{ '.MuiTablePagination-toolbar': { fontFamily: FONT_FAMILY } }}
                />
            </Paper>

            <Dialog open={transferDialog.open} onClose={() => setTransferDialog({ open: false })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>Transfer Stock</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Destination</InputLabel>
                            <Select value={transferDialog.to_location_id || ''} label="Destination"
                                onChange={(e) => setTransferDialog((d) => ({ ...d, to_location_id: e.target.value }))}>
                                {locations.filter((l) => l.id !== transferDialog.from_location_id)
                                    .map((l) => (
                                        <MenuItem key={l.id} value={l.id}>{l.code} — {l.name}</MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <TextField size="small" fullWidth type="number" label="Quantity"
                            value={transferDialog.quantity}
                            onChange={(e) => setTransferDialog((d) => ({ ...d, quantity: e.target.value }))} />
                        <TextField size="small" fullWidth label="Memo (optional)"
                            value={transferDialog.memo}
                            onChange={(e) => setTransferDialog((d) => ({ ...d, memo: e.target.value }))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTransferDialog({ open: false })}>Cancel</Button>
                    <Button variant="contained" disabled={transferring} onClick={handleTransfer}
                        sx={{ bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                        {transferring ? 'Transferring…' : 'Transfer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
